
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import type { ConsentStatus, ProcessingLog } from "./types";

export function useConsentManager() {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>({
    data_processing: false,
    data_storage: false,
    data_analysis: false,
  });
  const [processingLogs, setProcessingLogs] = useState<ProcessingLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const loadConsentStatus = async () => {
    try {
      // Query the view that provides a summary of user consents
      const { data: summary, error: summaryError } = await supabase
        .from('user_consent_summary')
        .select('*')
        .single();
        
      if (summaryError && summaryError.code !== 'PGRST116') {
        // PGRST116 is "Results contain 0 rows" - this is expected for new users
        throw summaryError;
      }

      if (summary) {
        setConsentStatus({
          data_processing: summary.data_processing || false,
          data_storage: summary.data_storage || false,
          data_analysis: summary.data_analysis || false,
        });
      }
    } catch (error) {
      console.error('Error loading consent status:', error);
      toast({
        variant: "destructive",
        title: "Feil ved lasting av samtykke",
        description: "Kunne ikke laste inn ditt nåværende samtykke. Vennligst prøv igjen senere."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadProcessingLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_data_processing')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setProcessingLogs(data || []);
    } catch (error) {
      console.error('Error loading processing logs:', error);
      toast({
        variant: "destructive",
        title: "Feil ved lasting av logg",
        description: "Kunne ikke laste inn behandlingshistorikk. Vennligst prøv igjen senere."
      });
    }
  };

  const updateConsent = async (type: keyof ConsentStatus) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      
      const newValue = !consentStatus[type];
      
      if (newValue) {
        // Grant consent
        const { error } = await supabase
          .from('user_ai_consent')
          .insert({
            consent_type: type.toString(),
            consented_at: new Date().toISOString(),
            user_id: user.id
          });

        if (error) throw error;
      } else {
        // Revoke consent
        const { error } = await supabase
          .from('user_ai_consent')
          .update({ revoked_at: new Date().toISOString() })
          .eq('consent_type', type.toString())
          .is('revoked_at', null);

        if (error) throw error;
      }

      setConsentStatus(prev => ({
        ...prev,
        [type]: newValue
      }));

      toast({
        title: newValue ? "Samtykke gitt" : "Samtykke trukket tilbake",
        description: `Du har ${newValue ? 'gitt' : 'trukket tilbake'} ditt samtykke til ${type.toString().replace('_', ' ')}.`
      });
    } catch (error) {
      console.error('Error updating consent:', error);
      toast({
        variant: "destructive",
        title: "Feil ved oppdatering av samtykke",
        description: "Kunne ikke oppdatere samtykke. Vennligst prøv igjen senere."
      });
    }
  };

  const requestDataDeletion = async () => {
    try {
      const { error } = await supabase.rpc('request_data_deletion');
      
      if (error) throw error;

      toast({
        title: "Forespørsel mottatt",
        description: "Din forespørsel om sletting av data er mottatt og vil bli behandlet."
      });
    } catch (error) {
      console.error('Error requesting data deletion:', error);
      toast({
        variant: "destructive",
        title: "Feil ved forespørsel",
        description: "Kunne ikke sende forespørsel om sletting. Vennligst prøv igjen senere."
      });
    }
  };

  useEffect(() => {
    loadConsentStatus();
    loadProcessingLogs();
  }, []);

  return {
    consentStatus,
    processingLogs,
    isLoading,
    updateConsent,
    requestDataDeletion
  };
}
